// Trade & Resell venture maths — buy low, sell high. The Buffett principle for kids:
// buy something for LESS than it's worth, sell it at FAIR value. Not speculation —
// the edge comes from research (spotting an undervalued buy), patience (holding
// through a cold market), and timing (selling when the market is hot), tempered by
// two real costs: a carrying cost every week you hold, and depreciation if you hold
// too long (fashion/tech goes out of date).
//
// The whole market is DERIVED from (rngSeed, week) — no offers are stored and the
// same week always shows the same shelf, so there's nothing to save-scum. The catch
// the player can't game: the market CONDITION of the FUTURE week they sell in is
// unknown when they buy. Predicted value is not guaranteed value.

import { nextFloat } from '../rng/prng.js';

export type TradeCategory = 'sneakers' | 'electronics' | 'collectibles' | 'equipment';

export const MAX_INVENTORY = 3; // hold at most 3 at once — forces prioritisation
export const MARKET_SIZE = 6; // items offered each week
export const CARRY_COST_PCT = 0.04; // weekly carrying cost = 4% of fair value
export const HOLD_GRACE_WEEKS = 3; // hold up to 3 weeks before depreciation bites

/** Weekly depreciation once past the grace period — tech dates fast, collectibles slow. */
const OBSOLESCENCE: Record<TradeCategory, number> = {
  electronics: 0.18,
  sneakers: 0.12,
  equipment: 0.1,
  collectibles: 0.04,
};

type CatalogEntry = {
  defId: string;
  name: string;
  emoji: string;
  category: TradeCategory;
  basePrice: number; // honest "fair value" anchor before weekly variance
  spread: number; // category volatility — width of the predicted resell range
};

const CATALOG: CatalogEntry[] = [
  { defId: 'sneaker-hype', name: 'Hype Sneakers', emoji: '👟', category: 'sneakers', basePrice: 80, spread: 0.36 },
  { defId: 'sneaker-retro', name: 'Retro Kicks', emoji: '👟', category: 'sneakers', basePrice: 50, spread: 0.3 },
  { defId: 'phone', name: 'Used Smartphone', emoji: '📱', category: 'electronics', basePrice: 120, spread: 0.26 },
  { defId: 'console', name: 'Game Console', emoji: '🕹️', category: 'electronics', basePrice: 90, spread: 0.28 },
  { defId: 'headphones', name: 'Headphones', emoji: '🎧', category: 'electronics', basePrice: 40, spread: 0.3 },
  { defId: 'cards', name: 'Trading Cards', emoji: '🃏', category: 'collectibles', basePrice: 30, spread: 0.5 },
  { defId: 'comic', name: 'Rare Comic', emoji: '📚', category: 'collectibles', basePrice: 25, spread: 0.55 },
  { defId: 'figure', name: 'Collector Figure', emoji: '🎎', category: 'collectibles', basePrice: 45, spread: 0.46 },
  { defId: 'bike', name: 'Mountain Bike', emoji: '🚲', category: 'equipment', basePrice: 110, spread: 0.22 },
  { defId: 'guitar', name: 'Electric Guitar', emoji: '🎸', category: 'equipment', basePrice: 95, spread: 0.24 },
  { defId: 'camera', name: 'Camera', emoji: '📷', category: 'equipment', basePrice: 130, spread: 0.24 },
  { defId: 'drone', name: 'Drone', emoji: '🚁', category: 'equipment', basePrice: 75, spread: 0.3 },
];

/** A single market offer — what the player sees on the shelf this week. */
export type TradeOffer = {
  slot: number; // 0..MARKET_SIZE-1 position on this week's shelf
  defId: string;
  name: string;
  emoji: string;
  category: TradeCategory;
  buyPrice: number; // what it costs to buy now
  fairValue: number; // honest worth — the Buffett anchor
  predictedLow: number; // predicted resell range (NOT guaranteed)
  predictedHigh: number;
};

const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));

/** Pure deterministic float for a catalog item in a given week — independent of the
 *  shared rng cursor so the market is a reproducible property of the week. */
function draw(seed: number, week: number, key: number, k: number): number {
  return nextFloat(seed, week * 100003 + key * 17 + k).value;
}

/** The 6 items on offer this week, derived from (seed, week). Same week → same shelf. */
export function generateMarket(seed: number, week: number): TradeOffer[] {
  const ranked = CATALOG.map((c, i) => ({ c, i, r: draw(seed, week, i, 0) }))
    .sort((a, b) => a.r - b.r)
    .slice(0, MARKET_SIZE);
  return ranked.map((entry, slot) => {
    const c = entry.c;
    const fairValue = Math.round(c.basePrice * (0.9 + 0.2 * draw(seed, week, entry.i, 1))); // ±10%
    const dealFactor = 0.7 + 0.35 * draw(seed, week, entry.i, 2); // [0.70, 1.05] of fair value
    const buyPrice = Math.round(fairValue * dealFactor);
    return {
      slot,
      defId: c.defId,
      name: c.name,
      emoji: c.emoji,
      category: c.category,
      buyPrice,
      fairValue,
      predictedLow: Math.round(fairValue * (1 - c.spread / 2)),
      predictedHigh: Math.round(fairValue * (1 + c.spread / 2)),
    };
  });
}

/** The market mood for a week, 0 (ice-cold buyers' market) … 1 (red-hot sellers').
 *  All items sold that week sit at this position in their predicted range. */
export function marketCondition(seed: number, week: number): number {
  return nextFloat(seed, week * 7919 + 31).value;
}

export type MarketMood = 'cold' | 'steady' | 'hot';
export function marketMood(condition: number): MarketMood {
  if (condition >= 0.66) return 'hot';
  if (condition >= 0.33) return 'steady';
  return 'cold';
}

/** Value haircut for holding too long. None inside the grace window; after that it
 *  falls at the category's obsolescence rate, floored so it's never worthless. */
export function depreciationFactor(category: TradeCategory, weeksHeld: number): number {
  const extra = Math.max(0, weeksHeld - HOLD_GRACE_WEEKS);
  if (extra <= 0) return 1;
  return Math.max(0.35, 1 - extra * OBSOLESCENCE[category]);
}

/** Weekly carrying cost of holding an item (a positive magnitude). */
export function carryCost(fairValue: number): number {
  return Math.max(1, Math.round(fairValue * CARRY_COST_PCT));
}

type Resellable = {
  category: TradeCategory;
  predictedLow: number;
  predictedHigh: number;
};

/** Actual resell price = position in the predicted range (from the week's market
 *  condition) AFTER the depreciation haircut for how long it was held. */
export function resellValue(item: Resellable, weeksHeld: number, condition: number): number {
  const inRange = item.predictedLow + (item.predictedHigh - item.predictedLow) * clamp01(condition);
  return Math.max(0, Math.round(inRange * depreciationFactor(item.category, weeksHeld)));
}
